import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { alpha, useTheme } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts/BarChart";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { Link as RouterLink } from "react-router-dom";
import UsageApi, {
  UsageByHaloCategoryRow,
  UsageByHaloCategoryTicketRow,
  UsageByTicketRow,
  UsageByToolRow,
  UsageByUserAssistantRow,
  UsageByUserTicketsRow,
  UsageDailyRow,
  UsageFilterParams,
  UsageResponse,
  UsageSummary,
} from "../apis/usageApi";
import type { UsageQuickDatePresetId } from "../apis/usageApiUtils";
import {
  expandDailyUsageForRange,
  getUsageDateRangeForPreset,
  USAGE_DEFAULT_QUICK_PRESET,
  USAGE_QUICK_RANGE_OPTIONS,
} from "../apis/usageApiUtils";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import { useUserMsp } from "../hooks/useUserMsp";
import { canAccessUsageAnalytics, isHorizonRootRole } from "../utils/horizonRoles";
import { isHalo } from "../utils/mspUtils";
import {
  aggregateUsageByCategory,
  buildUsageInsightsSummaryText,
  estimatedMinutesForToolAction,
  formatUsageInsightsHours,
  humanizeUsageToolName,
  sumEstimatedMinutesFromToolRows,
} from "../utils/usageInsightsUtils";
import { getTicketTheoUrl } from "./automatableDashboardShared";
import UsageTimeSavedEstimateHelp from "./UsageTimeSavedEstimateHelp";

const DEFAULT_SUMMARY: UsageSummary = {
  actions: 0,
  ticket_actions: 0,
  assistant_actions: 0,
  tickets_touched: 0,
};

const QUICK_RANGE_SELECT_OPTIONS = USAGE_QUICK_RANGE_OPTIONS.map((o) => ({
  value: o.id,
  label: o.label,
}));

const usageInitialDateRange = getUsageDateRangeForPreset(USAGE_DEFAULT_QUICK_PRESET)!;

type UsageLoadRequest = {
  key: string;
  params: UsageFilterParams;
};

function usageLoadRequestKey(params: UsageFilterParams): string {
  return JSON.stringify({
    startDate: params.startDate ?? null,
    endDate: params.endDate ?? null,
    userEmail: params.userEmail ? String(params.userEmail).trim().toLowerCase() : null,
    limit: params.limit ?? null,
    includeRootUsers: params.includeRootUsers === true,
    mspId: params.mspId ?? null,
  });
}

const cardSx = {
  borderRadius: "10px",
  border: "1px solid #E2E8F0",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  height: "100%",
};

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: "1.05rem",
  color: "#334155",
  letterSpacing: "-0.01em",
};

type HaloCategoryDrilldownMode = "theo" | "total";

function HaloCategoryTicketsTable({
  tickets,
  emptyActionsWhenNoTheo = false,
}: {
  tickets: UsageByHaloCategoryTicketRow[];
  /** When true, leave Actions / Est. time blank for tickets Theo did not touch. */
  emptyActionsWhenNoTheo?: boolean;
}) {
  if (tickets.length === 0) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Typography color="text.secondary">No ticket details available for this category.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 560 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Ticket</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 260 }}>Actions (in order)</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              Est. time saved
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tickets.map((ticket) => {
            const ticketId = String(ticket.ticket_id || "").trim();
            const title = String(ticket.ticket_title || "").trim();
            const status = String(ticket.status_name || "").trim();
            const client = String(ticket.client_name || "").trim();
            const actionRuns = Array.isArray(ticket.action_runs) ? ticket.action_runs : [];
            const hasTheoActions = actionRuns.length > 0 || Number(ticket.actions || 0) > 0;
            return (
              <TableRow key={ticketId || title} hover>
                <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                  {ticketId ? (
                    <Link
                      href={getTicketTheoUrl(ticketId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontWeight: 700 }}
                    >
                      {ticketId}
                    </Link>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>{status || "—"}</TableCell>
                <TableCell sx={{ verticalAlign: "top", overflowWrap: "anywhere" }}>{client || "—"}</TableCell>
                <TableCell sx={{ verticalAlign: "top", overflowWrap: "anywhere" }}>{title || "—"}</TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  {actionRuns.length > 0 ? (
                    <Box
                      component="ol"
                      sx={{
                        m: 0,
                        pl: 2.25,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.35,
                      }}
                    >
                      {actionRuns.map((run, index) => {
                        const toolName = String(run.tool_name || "").trim() || "(unknown)";
                        const runKey = `${ticketId}-${run.audit_id ?? index}-${toolName}`;
                        return (
                          <Box
                            component="li"
                            key={runKey}
                            sx={{
                              fontSize: "0.8125rem",
                              color: "#334155",
                              lineHeight: 1.35,
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {humanizeUsageToolName(toolName)}
                            </Box>
                            <Box component="span" sx={{ color: "#64748B" }}>
                              {" — "}
                              {formatUsageInsightsHours(Number(run.estimated_minutes_saved) || 0)}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : emptyActionsWhenNoTheo && !hasTheoActions ? (
                    ""
                  ) : (
                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748B" }}>
                      {Number(ticket.actions || 0).toLocaleString()} actions
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                  {emptyActionsWhenNoTheo && !hasTheoActions
                    ? ""
                    : formatUsageInsightsHours(Number(ticket.estimated_minutes_saved) || 0)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function KpiCard({
  icon,
  label,
  labelHelp,
  value,
  caption,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  labelHelp?: React.ReactNode;
  value: string;
  caption: string;
  accent: string;
}) {
  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(accent, 0.12),
              color: accent,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#64748B" }}>{label}</Typography>
            {labelHelp}
          </Box>
        </Box>
        <Typography sx={{ fontSize: "2rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.1, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748B", lineHeight: 1.4 }}>{caption}</Typography>
      </CardContent>
    </Card>
  );
}

const MspUsageInsightsTab: React.FC = () => {
  const theme = useTheme();
  const { userMspInfo, isLoading: userMspLoading } = useUserMsp();
  const canView = canAccessUsageAnalytics(userMspInfo?.role);
  const isRootViewer = isHorizonRootRole(userMspInfo?.role);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickPreset, setQuickPreset] = useState<UsageQuickDatePresetId>(USAGE_DEFAULT_QUICK_PRESET);
  const [startDate, setStartDate] = useState(usageInitialDateRange.startDate);
  const [endDate, setEndDate] = useState(usageInitialDateRange.endDate);
  const [summary, setSummary] = useState<UsageSummary>(DEFAULT_SUMMARY);
  const [usageByTool, setUsageByTool] = useState<UsageByToolRow[]>([]);
  const [usageByUserTickets, setUsageByUserTickets] = useState<UsageByUserTicketsRow[]>([]);
  const [usageByUserAssistant, setUsageByUserAssistant] = useState<UsageByUserAssistantRow[]>([]);
  const [usageByTicket, setUsageByTicket] = useState<UsageByTicketRow[]>([]);
  const [usageByHaloCategory, setUsageByHaloCategory] = useState<UsageByHaloCategoryRow[]>([]);
  const activeUsageRequestKeyRef = useRef<string | null>(null);
  const queuedUsageRequestRef = useRef<UsageLoadRequest | null>(null);
  const [haloCategoryDrilldown, setHaloCategoryDrilldown] = useState<{
    row: UsageByHaloCategoryRow;
    mode: HaloCategoryDrilldownMode;
    tickets: UsageByHaloCategoryTicketRow[];
    loading: boolean;
    error: string | null;
  } | null>(null);

  const openHaloCategoryDrilldown = useCallback(
    async (row: UsageByHaloCategoryRow, mode: HaloCategoryDrilldownMode) => {
      setHaloCategoryDrilldown({
        row,
        mode,
        tickets: [],
        loading: true,
        error: null,
      });

      try {
        const payload = await UsageApi.getHaloCategoryTickets({
          categoryId: row.category_id,
          categoryName: row.category_name,
          mode,
          startDate,
          endDate,
          userEmail: null,
          includeRootUsers: false,
          mspId: isRootViewer ? Number(userMspInfo?.msp_id) : null,
        });
        setHaloCategoryDrilldown((prev) => {
          if (!prev || prev.mode !== mode || prev.row.category_id !== row.category_id) return prev;
          const nextName = String(payload.category_name || "").trim();
          const prevName = String(prev.row.category_name || "").trim();
          // Never replace a strong table label with a weak "Category N" fallback.
          const nextIsWeak = !nextName || /^category\s+\d+$/i.test(nextName);
          const prevIsStrong = Boolean(prevName) && !/^category\s+\d+$/i.test(prevName);
          const shouldUpdateName = Boolean(nextName) && nextName !== prevName && !(nextIsWeak && prevIsStrong);
          return {
            ...prev,
            row: shouldUpdateName ? { ...prev.row, category_name: nextName } : prev.row,
            tickets: Array.isArray(payload.tickets) ? payload.tickets : [],
            loading: false,
            error: null,
          };
        });
      } catch (err: any) {
        setHaloCategoryDrilldown((prev) => {
          if (!prev || prev.mode !== mode || prev.row.category_id !== row.category_id) return prev;
          return {
            ...prev,
            loading: false,
            error: err?.userMessage || err?.message || "Failed to load tickets",
          };
        });
      }
    },
    [startDate, endDate, isRootViewer, userMspInfo?.msp_id]
  );
  const [hasHaloPsa, setHasHaloPsa] = useState(false);
  const [dailyTickets, setDailyTickets] = useState<UsageDailyRow[]>([]);
  const [dailyAssistant, setDailyAssistant] = useState<UsageDailyRow[]>([]);

  const runUsageRequest = useCallback(
    async (initialRequest: UsageLoadRequest) => {
      let request: UsageLoadRequest | null = initialRequest;
      while (request) {
        if (activeUsageRequestKeyRef.current) {
          queuedUsageRequestRef.current = request;
          return;
        }

        activeUsageRequestKeyRef.current = request.key;
        setLoading(true);
        setError(null);
        try {
          const response: UsageResponse = await UsageApi.getUsage(request.params);

          setSummary({
            actions: Number(response.summary?.actions) || 0,
            ticket_actions: Number(response.summary?.ticket_actions) || 0,
            assistant_actions: Number(response.summary?.assistant_actions) || 0,
            tickets_touched: Number(response.summary?.tickets_touched) || 0,
          });
          setUsageByTool(Array.isArray(response.usage_by_tool) ? response.usage_by_tool : []);
          setUsageByUserTickets(Array.isArray(response.usage_by_user_tickets) ? response.usage_by_user_tickets : []);
          setUsageByUserAssistant(
            Array.isArray(response.usage_by_user_assistant) ? response.usage_by_user_assistant : []
          );
          setUsageByTicket(Array.isArray(response.usage_by_ticket) ? response.usage_by_ticket : []);
          const haloEnabled =
            response.has_halo_psa === true ||
            (typeof response.has_halo_psa !== "boolean" &&
              Boolean(userMspInfo?.msp_type && isHalo(String(userMspInfo.msp_type).toLowerCase())));
          setHasHaloPsa(haloEnabled);
          setUsageByHaloCategory(
            haloEnabled && Array.isArray(response.usage_by_halo_category) ? response.usage_by_halo_category : []
          );
          setDailyTickets(Array.isArray(response.daily_tickets) ? response.daily_tickets : []);
          setDailyAssistant(Array.isArray(response.daily_assistant) ? response.daily_assistant : []);
        } catch (err: any) {
          setError(err?.userMessage || err?.message || "Failed to load usage insights");
        } finally {
          if (activeUsageRequestKeyRef.current === request.key) {
            activeUsageRequestKeyRef.current = null;
          }
        }

        const queued = queuedUsageRequestRef.current;
        queuedUsageRequestRef.current = null;
        if (queued && queued.key !== request.key) {
          request = queued;
          continue;
        }
        request = null;
      }
      setLoading(false);
    },
    [userMspInfo?.msp_type]
  );

  const loadUsage = useCallback(async () => {
    if (!canView || userMspInfo?.msp_id == null) {
      setLoading(false);
      return;
    }

    const params: UsageFilterParams = {
      startDate,
      endDate,
      userEmail: null,
      limit: 500,
      includeRootUsers: false,
      mspId: isRootViewer ? Number(userMspInfo.msp_id) : null,
    };
    await runUsageRequest({
      key: usageLoadRequestKey(params),
      params,
    });
  }, [canView, userMspInfo?.msp_id, isRootViewer, startDate, endDate, runUsageRequest]);

  useEffect(() => {
    if (!userMspLoading) {
      void loadUsage();
    }
  }, [userMspLoading, loadUsage]);

  const categoryRows = useMemo(() => aggregateUsageByCategory(usageByTool), [usageByTool]);
  const estimatedMinutesSaved = useMemo(() => sumEstimatedMinutesFromToolRows(usageByTool), [usageByTool]);
  const activeUsers = useMemo(() => {
    const emails = new Set<string>();
    for (const row of usageByUserTickets) {
      const email = String(row.user_email || "").trim();
      if (email) emails.add(email);
    }
    for (const row of usageByUserAssistant) {
      const email = String(row.user_email || "").trim();
      if (email) emails.add(email);
    }
    return emails.size;
  }, [usageByUserTickets, usageByUserAssistant]);

  const ticketsTouched =
    Number(summary.tickets_touched) > 0 ? Number(summary.tickets_touched) : usageByTicket.length;
  const summaryText = useMemo(
    () =>
      buildUsageInsightsSummaryText({
        totalActions: summary.actions,
        ticketActions: summary.ticket_actions,
        assistantActions: summary.assistant_actions,
        ticketsTouched,
        activeUsers,
        estimatedMinutesSaved,
        topCategoryLabel: categoryRows[0]?.label,
      }),
    [summary, ticketsTouched, activeUsers, estimatedMinutesSaved, categoryRows]
  );

  const chartData = useMemo(() => {
    const ticketRows = expandDailyUsageForRange(startDate, endDate, dailyTickets, "asc");
    const assistantRows = expandDailyUsageForRange(startDate, endDate, dailyAssistant, "asc");
    const assistantByDate = new Map(assistantRows.map((row) => [row.date || "", Number(row.actions) || 0]));
    return ticketRows.map((row) => {
      const date = row.date || "";
      return {
        date,
        actions: (Number(row.actions) || 0) + (assistantByDate.get(date) || 0),
      };
    });
  }, [startDate, endDate, dailyTickets, dailyAssistant]);

  const topTools = useMemo(
    () => [...usageByTool].sort((a, b) => (Number(b.actions) || 0) - (Number(a.actions) || 0)).slice(0, 10),
    [usageByTool]
  );

  const topUsers = useMemo(() => {
    const byEmail = new Map<string, number>();
    for (const row of usageByUserTickets) {
      const email = String(row.user_email || "Unknown").trim() || "Unknown";
      byEmail.set(email, (byEmail.get(email) || 0) + (Number(row.actions) || 0));
    }
    for (const row of usageByUserAssistant) {
      const email = String(row.user_email || "Unknown").trim() || "Unknown";
      byEmail.set(email, (byEmail.get(email) || 0) + (Number(row.actions) || 0));
    }
    return Array.from(byEmail.entries())
      .map(([email, actions]) => ({ email, actions }))
      .sort((a, b) => b.actions - a.actions)
      .slice(0, 8);
  }, [usageByUserTickets, usageByUserAssistant]);

  const handleQuickPresetChange = (value: string) => {
    const preset = value as UsageQuickDatePresetId;
    setQuickPreset(preset);
    if (preset === "custom") return;
    const range = getUsageDateRangeForPreset(preset);
    if (!range) return;
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  if (userMspLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="info">
        Usage Insights is available to MSP administrators. Ask your admin for access, or visit the Usage page from the
        sidebar when your role allows it.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>Usage Insights</Typography>
        <Typography sx={{ color: "#64748B", fontSize: "0.9375rem", lineHeight: 1.5, maxWidth: 760 }}>
          A summary of work Theo completed for your MSP, with estimated technician hours saved based on typical handle
          time per action category.
        </Typography>
      </Box>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 2.25 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <LayeredAutocompleteSelect
                label="Quick range"
                value={quickPreset}
                options={QUICK_RANGE_SELECT_OPTIONS}
                onChange={handleQuickPresetChange}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2.5}>
              <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#64748B" }}>
                Start date
              </Typography>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setQuickPreset("custom");
                  setStartDate(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #CBD5E1",
                  fontSize: 14,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2.5}>
              <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#64748B" }}>
                End date
              </Typography>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setQuickPreset("custom");
                  setEndDate(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #CBD5E1",
                  fontSize: 14,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ display: "block", color: "#64748B", lineHeight: 1.45 }}>
                Need row-level audit detail? Open{" "}
                <Link component={RouterLink} to="/usage" underline="hover" sx={{ fontWeight: 600 }}>
                  Usage
                </Link>{" "}
                or{" "}
                <Link component={RouterLink} to="/audit-logs" underline="hover" sx={{ fontWeight: 600 }}>
                  Audit Logs
                </Link>
                .
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<AccessTimeRoundedIcon fontSize="small" />}
                label="Estimated hours saved"
                labelHelp={<UsageTimeSavedEstimateHelp />}
                value={formatUsageInsightsHours(estimatedMinutesSaved)}
                caption="Based on typical handle time per action type"
                accent={theme.palette.custom.chartGreen}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<AutoFixHighRoundedIcon fontSize="small" />}
                label="Total actions"
                value={summary.actions.toLocaleString()}
                caption={`${summary.ticket_actions.toLocaleString()} ticket · ${summary.assistant_actions.toLocaleString()} assistant`}
                accent="#2563EB"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<ConfirmationNumberRoundedIcon fontSize="small" />}
                label="Tickets touched"
                value={ticketsTouched.toLocaleString()}
                caption="Distinct PSA tickets with Theo tool runs"
                accent="#7C3AED"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<GroupsRoundedIcon fontSize="small" />}
                label="Active technicians"
                value={activeUsers.toLocaleString()}
                caption="Users with ticket or assistant activity"
                accent="#EA580C"
              />
            </Grid>
          </Grid>

          <Card sx={cardSx}>
            <CardContent sx={{ p: 2.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <InsightsRoundedIcon sx={{ color: theme.palette.custom.chartGreen }} />
                <Typography sx={sectionTitleSx}>Executive summary</Typography>
              </Box>
              <Typography sx={{ color: "#334155", fontSize: "0.95rem", lineHeight: 1.6 }}>{summaryText}</Typography>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 2.25 }}>
                  <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Work by category</Typography>
                  {categoryRows.length === 0 ? (
                    <Typography color="text.secondary">No Theo actions in this period.</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell align="right">Actions</TableCell>
                            <TableCell align="right">
                              <Box
                                component="span"
                                sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}
                              >
                                Est. time saved
                                <UsageTimeSavedEstimateHelp />
                              </Box>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {categoryRows.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>
                                <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{row.label}</Typography>
                                <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>{row.description}</Typography>
                              </TableCell>
                              <TableCell align="right">{row.actions.toLocaleString()}</TableCell>
                              <TableCell align="right">{formatUsageInsightsHours(row.estimatedMinutesSaved)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 2.25 }}>
                  <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Top technicians</Typography>
                  {topUsers.length === 0 ? (
                    <Typography color="text.secondary">No user activity in this period.</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topUsers.map((row) => (
                            <TableRow key={row.email} hover>
                              <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {row.email}
                              </TableCell>
                              <TableCell align="right">{row.actions.toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {hasHaloPsa && (
            <Card sx={cardSx}>
              <CardContent sx={{ p: 2.25 }}>
                <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>Work by Halo Category</Typography>
                <Typography sx={{ color: "#64748B", fontSize: "0.8125rem", mb: 2, lineHeight: 1.45 }}>
                  Ticket actions rolled up by Halo PSA Category (not Ticket Type). Uses ingested ticket category data;
                  recent tickets missing a category are filled from live Halo ticket details when available.
                </Typography>
                {usageByHaloCategory.length === 0 ? (
                  <Typography color="text.secondary">
                    No Halo category tickets created, updated, or touched by Theo in this period.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Halo Category</TableCell>
                          <TableCell align="right">
                            <Box
                              component="span"
                              sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}
                            >
                              Theo tickets
                              <Tooltip
                                title="Tickets in this Halo category that Theo took actions on in the selected period. Click the count to drill down."
                                arrow
                                placement="top"
                              >
                                <HelpOutlineRoundedIcon sx={{ fontSize: 15, color: "#94A3B8", cursor: "help" }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              component="span"
                              sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}
                            >
                              Total tickets
                              <Tooltip
                                title="Halo tickets created or updated in this period in this category, including tickets Theo worked in the period. Click to list tickets; Theo actions are shown when present."
                                arrow
                                placement="top"
                              >
                                <HelpOutlineRoundedIcon sx={{ fontSize: 15, color: "#94A3B8", cursor: "help" }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              component="span"
                              sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}
                            >
                              % covered
                              <Tooltip
                                title="Theo tickets ÷ Total tickets (created/updated in period ∪ Theo-touched), capped at 100%."
                                arrow
                                placement="top"
                              >
                                <HelpOutlineRoundedIcon sx={{ fontSize: 15, color: "#94A3B8", cursor: "help" }} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="right">Actions</TableCell>
                          <TableCell align="right">
                            <Box
                              component="span"
                              sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}
                            >
                              Est. time saved
                              <UsageTimeSavedEstimateHelp />
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageByHaloCategory.map((row) => (
                          <TableRow key={row.category_id} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{row.category_name}</Typography>
                              {row.category_id &&
                                row.category_id !== "(uncategorized)" &&
                                row.category_name !== row.category_id &&
                                !new RegExp(`^Category\\s+${String(row.category_id).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i").test(
                                  String(row.category_name || "")
                                ) && (
                                  <Typography sx={{ fontSize: "0.7rem", color: "#94A3B8" }}>ID {row.category_id}</Typography>
                                )}
                            </TableCell>
                            <TableCell align="right">
                              {Number(row.ticket_count) > 0 ? (
                                <Link
                                  component="button"
                                  type="button"
                                  underline="hover"
                                  sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                                  onClick={() => void openHaloCategoryDrilldown(row, "theo")}
                                >
                                  {Number(row.ticket_count).toLocaleString()}
                                </Link>
                              ) : (
                                <Typography
                                  component="span"
                                  sx={{ fontVariantNumeric: "tabular-nums", color: "#94A3B8" }}
                                >
                                  {Number(row.ticket_count).toLocaleString()}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                              {row.category_ticket_count != null &&
                              Number.isFinite(Number(row.category_ticket_count)) &&
                              Number(row.category_ticket_count) > 0 ? (
                                <Link
                                  component="button"
                                  type="button"
                                  underline="hover"
                                  sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                                  onClick={() => void openHaloCategoryDrilldown(row, "total")}
                                >
                                  {Number(row.category_ticket_count).toLocaleString()}
                                </Link>
                              ) : row.category_ticket_count != null &&
                                Number.isFinite(Number(row.category_ticket_count)) ? (
                                Number(row.category_ticket_count).toLocaleString()
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                              {row.ticket_coverage_pct != null && Number.isFinite(Number(row.ticket_coverage_pct))
                                ? `${Number(row.ticket_coverage_pct).toLocaleString(undefined, {
                                    maximumFractionDigits: 1,
                                  })}%`
                                : "—"}
                            </TableCell>
                            <TableCell align="right">{Number(row.actions).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              {formatUsageInsightsHours(Number(row.estimated_minutes_saved) || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          <Dialog
            open={haloCategoryDrilldown != null}
            onClose={() => setHaloCategoryDrilldown(null)}
            maxWidth="lg"
            fullWidth
            scroll="paper"
          >
            <DialogTitle sx={{ pr: 6 }}>
              {haloCategoryDrilldown
                ? (() => {
                    const { row, mode, tickets, loading } = haloCategoryDrilldown;
                    // While loading, show the table cell count; after load, show the
                    // returned list length (server drilldown is aligned to table semantics).
                    const tableCount =
                      mode === "total"
                        ? Number(row.category_ticket_count || 0)
                        : Number(row.ticket_count || 0);
                    const count = loading ? tableCount : tickets.length || tableCount;
                    const noun =
                      mode === "total"
                        ? count === 1
                          ? "total ticket"
                          : "total tickets"
                        : count === 1
                          ? "Theo ticket"
                          : "Theo tickets";
                    return `${row.category_name} — ${count.toLocaleString()} ${noun}`;
                  })()
                : ""}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              {haloCategoryDrilldown?.loading ? (
                <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={28} />
                </Box>
              ) : haloCategoryDrilldown?.error ? (
                <Box sx={{ p: 2.5 }}>
                  <Typography color="error.main">{haloCategoryDrilldown.error}</Typography>
                </Box>
              ) : (
                <HaloCategoryTicketsTable
                  tickets={haloCategoryDrilldown?.tickets || []}
                  emptyActionsWhenNoTheo={haloCategoryDrilldown?.mode === "total"}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHaloCategoryDrilldown(null)}>Close</Button>
            </DialogActions>
          </Dialog>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 2.25 }}>
                  <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Daily activity</Typography>
                  {chartData.length === 0 ? (
                    <Typography color="text.secondary">No daily activity in this period.</Typography>
                  ) : (
                    <Box sx={{ width: "100%", height: 280 }}>
                      <BarChart
                        xAxis={[
                          {
                            scaleType: "band",
                            data: chartData.map((row) => row.date),
                            tickLabelStyle: { fontSize: 11 },
                          },
                        ]}
                        series={[
                          {
                            data: chartData.map((row) => row.actions),
                            label: "Actions",
                            color: theme.palette.custom.chartGreen,
                          },
                        ]}
                        height={280}
                        margin={{ left: 48, right: 16, top: 24, bottom: 56 }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card sx={cardSx}>
                <CardContent sx={{ p: 2.25 }}>
                  <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Top tools</Typography>
                  {topTools.length === 0 ? (
                    <Typography color="text.secondary">No tool runs in this period.</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tool</TableCell>
                            <TableCell align="right">Runs</TableCell>
                            <TableCell align="right">
                              <Box
                                component="span"
                                sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}
                              >
                                Est. time saved
                                <UsageTimeSavedEstimateHelp />
                              </Box>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topTools.map((row) => (
                            <TableRow key={row.tool_name} hover>
                              <TableCell>
                                <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem" }}>
                                  {humanizeUsageToolName(row.tool_name)}
                                </Typography>
                                <Typography sx={{ fontSize: "0.7rem", color: "#94A3B8" }}>{row.tool_name}</Typography>
                              </TableCell>
                              <TableCell align="right">{Number(row.actions).toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {formatUsageInsightsHours(
                                  (Number(row.actions) || 0) * estimatedMinutesForToolAction(row.tool_name)
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default MspUsageInsightsTab;
